import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Plus, Search, Edit2, Trash2, Radio, Activity, AlertTriangle,
  CheckCircle, Clock, User, MapPin, Building2,
  Calendar, Eye, Copy, Printer, Signal, Battery
} from 'lucide-react';
import Modal from './Modal';

interface RadioDevice {
  id: number;
  radio_name: string;
  brand: string;
  model: string;
  serial_number: string;
  inventory_number?: string;
  location_id: number;
  location?: { id: number; name: string };
  company_id: number;
  company?: { id: number; name: string };
  assigned_user_id?: number;
  assigned_user?: { id: number; name: string };
  status: 'active' | 'inactive' | 'maintenance' | 'faulty';
  health_status?: string;
  frequency?: string;
  frequency_range?: string;
  power_output?: string;
  battery_type?: string;
  battery_level?: number;
  purchase_date?: string;
  warranty_end_date?: string;
  purchase_price?: number;
  currency?: 'TRY' | 'USD' | 'EUR';
  image_path?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

const RadiosPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingRadio, setEditingRadio] = useState<RadioDevice | null>(null);
  const [selectedRadio, setSelectedRadio] = useState<RadioDevice | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [radiosData, setRadiosData] = useState<RadioDevice[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [radioData, setRadioData] = useState({
    radio_name: '',
    brand: '',
    model: '',
    serial_number: '',
    inventory_number: '',
    location_id: 0,
    company_id: 0,
    assigned_user_id: 0,
    status: 'active' as RadioDevice['status'],
    frequency: '',
    frequency_range: '',
    power_output: '',
    battery_type: '',
    purchase_date: '',
    warranty_end_date: '',
    purchase_price: 0,
    currency: 'TRY' as 'TRY' | 'USD' | 'EUR',
    notes: ''
  });

  React.useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir telsiz açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'radios' || !e.detail?.entityId) return;
      try {
        const radio = await apiService.getRadio(e.detail.entityId);
        if (radio) { setSelectedRadio(radio); setShowDetailModal(true); }
      } catch (err) { console.error('[openEntityById radios]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const usersPromise = apiService.getUsers().catch(() => []);
      const [radiosResponse, companiesResponse, locationsResponse, usersResponse] = await Promise.all([
        apiService.getRadios(),
        apiService.getCompanies(),
        apiService.getLocations(),
        usersPromise
      ]);
      setRadiosData(Array.isArray(radiosResponse) ? radiosResponse : []);
      setCompaniesData(Array.isArray(companiesResponse) ? companiesResponse : []);
      const locs = locationsResponse?.data ?? (Array.isArray(locationsResponse) ? locationsResponse : []);
      setLocationsData(locs);
      setUsersData(Array.isArray(usersResponse) ? usersResponse : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('radios.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'active', label: t('radios.statusActive'), color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: t('radios.statusInactive'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    { value: 'maintenance', label: t('radios.statusMaintenance'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'faulty', label: t('radios.statusFaulty'), color: 'bg-red-100 text-red-800' }
  ];

  const filteredRadios = radiosData.filter(radio => {
    const matchesSearch =
      String(radio.radio_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(radio.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(radio.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(radio.serial_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(radio.inventory_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || radio.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleEdit = (radio: RadioDevice) => {
    setEditingRadio(radio);
    setRadioData({
      radio_name: radio.radio_name || '',
      brand: radio.brand || '',
      model: radio.model || '',
      serial_number: radio.serial_number || '',
      inventory_number: radio.inventory_number || '',
      location_id: radio.location?.id || radio.location_id || 0,
      company_id: radio.company?.id || radio.company_id || 0,
      assigned_user_id: radio.assigned_user_id || 0,
      status: radio.status || 'active',
      frequency: radio.frequency || '',
      frequency_range: radio.frequency_range || '',
      power_output: radio.power_output || '',
      battery_type: radio.battery_type || '',
      purchase_date: radio.purchase_date ? radio.purchase_date.split('T')[0] : '',
      warranty_end_date: radio.warranty_end_date ? radio.warranty_end_date.split('T')[0] : '',
      purchase_price: radio.purchase_price || 0,
      currency: radio.currency || 'TRY',
      notes: radio.notes || ''
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingRadio(null);
    setRadioData({
      radio_name: '',
      brand: '',
      model: '',
      serial_number: '',
      inventory_number: '',
      location_id: 0,
      company_id: 0,
      assigned_user_id: 0,
      status: 'active',
      frequency: '',
      frequency_range: '',
      power_output: '',
      battery_type: '',
      purchase_date: '',
      warranty_end_date: '',
      purchase_price: 0,
      currency: 'TRY',
      notes: ''
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleCopy = (radio: RadioDevice) => {
    setEditingRadio(null);
    setRadioData({
      radio_name: `${radio.radio_name} ${t('radios.copyNameSuffix')}`,
      brand: radio.brand || '',
      model: radio.model || '',
      serial_number: '',
      inventory_number: '',
      location_id: radio.location?.id || radio.location_id || 0,
      company_id: radio.company?.id || radio.company_id || 0,
      assigned_user_id: 0,
      status: 'active',
      frequency: radio.frequency || '',
      frequency_range: radio.frequency_range || '',
      power_output: radio.power_output || '',
      battery_type: radio.battery_type || '',
      purchase_date: '',
      warranty_end_date: '',
      purchase_price: radio.purchase_price || 0,
      currency: radio.currency || 'TRY',
      notes: radio.notes || ''
    });
    setImageFile(null);
    setShowModal(true);
  };

  const checkDuplicateSerialNumber = (serial: string, excludeId?: number) => {
    if (!serial) return false;
    return radiosData.some(r =>
      String(r.serial_number || '').toLowerCase() === String(serial || '').toLowerCase() &&
      r.id !== excludeId
    );
  };

  const handleSave = async () => {
    if (!radioData.radio_name || !radioData.brand || !radioData.model || !radioData.serial_number) {
      showGlobalNotification.error(t('common.error'), t('radios.errorRequired'));
      return;
    }
    if (!radioData.location_id || !radioData.company_id) {
      showGlobalNotification.error(t('common.error'), t('radios.errorLocationCompany'));
      return;
    }
    if (checkDuplicateSerialNumber(radioData.serial_number, editingRadio?.id)) {
      showGlobalNotification.error(t('common.error'), t('radios.errorDuplicateSerial'));
      return;
    }

    try {
      const formData = new FormData();
      Object.keys(radioData).forEach(key => {
        const value = radioData[key as keyof typeof radioData];
        if (value !== null && value !== undefined && value !== '' && value !== 0) {
          formData.append(key, String(value));
        }
      });
      // Always send required numeric fields even if 0
      formData.set('location_id', String(radioData.location_id));
      formData.set('company_id', String(radioData.company_id));
      formData.set('status', radioData.status);
      formData.set('currency', radioData.currency);
      if (radioData.assigned_user_id > 0) {
        formData.set('assigned_user_id', String(radioData.assigned_user_id));
      }
      if (imageFile) {
        formData.append('image', imageFile);
      }

      if (editingRadio) {
        await apiService.updateRadio(editingRadio.id, formData);
        showGlobalNotification.success(t('common.success'), t('radios.successUpdated'));
      } else {
        await apiService.createRadio(formData);
        showGlobalNotification.success(t('common.success'), t('radios.successCreated'));
      }
      await loadData();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving radio:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('radios.errorSave'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t('radios.confirmDelete'))) return;
    try {
      await apiService.deleteRadio(id);
      showGlobalNotification.success(t('common.success'), t('radios.successDeleted'));
      await loadData();
    } catch (error: any) {
      showGlobalNotification.error(t('common.error'), error.message || t('radios.errorDelete'));
    }
  };

  const generateRadioQRCode = async (radio: RadioDevice) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/radios/${radio.id}/qr-code?token=${token}&embed=true`
    );
    if (!response.ok) throw new Error('QR kod oluşturulamadı');
    const html = await response.text();
    const match = html.match(/src="([^"]+)"/);
    if (!match) throw new Error('QR kod bulunamadı');
    return match[1];
  };

  const printRadioLabel = async (radio: RadioDevice) => {
    try {
      const qrCodeDataURL = await generateRadioQRCode(radio);
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Telsiz Etiketi - ${radio.radio_name}</title>
          <style>
            @page { size: A4; margin: 10mm; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
            .page { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; gap: 10mm; height: 267mm; page-break-after: always; }
            .page:last-child { page-break-after: avoid; }
            .label { border: 2px solid #333; border-radius: 8px; padding: 6mm; display: flex; flex-direction: column; align-items: center; text-align: center; background: white; }
            .label-title { font-size: 16px; font-weight: bold; color: #666; margin-bottom: 2mm; }
            .label-value { font-size: 26px; font-weight: bold; color: #333; font-family: monospace; letter-spacing: 1px; }
            .device-name { font-size: 24px; font-weight: bold; color: #333; margin: 3mm 0; }
            .qr-code { width: 40mm; height: 40mm; margin: 3mm 0; }
            .device-info { font-size: 20px; color: #666; line-height: 1.4; }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="label">
              <div class="label-title">${t('radios.detailName').replace(':', '')}</div>
              <div class="label-value">${radio.radio_name}</div>
              <div class="device-name">${radio.brand} ${radio.model}</div>
              <img class="qr-code" src="${qrCodeDataURL}" alt="QR Code" />
              <div class="device-info">
                ${radio.serial_number ? `📻 ${radio.serial_number}<br>` : ''}
                ${radio.inventory_number ? `📋 ${radio.inventory_number}<br>` : ''}
                ${radio.frequency ? `📡 ${radio.frequency}` : ''}
              </div>
            </div>
            <div class="label" style="border:none;"></div>
            <div class="label" style="border:none;"></div>
            <div class="label" style="border:none;"></div>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 1000);
    } catch (error) {
      showGlobalNotification.error(t('common.error'), t('radios.errorPrint'));
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('radios.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('radios.subtitle')}</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('radios.addNew')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('radios.totalRadios')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{radiosData.length}</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
              <Radio className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('radios.active')}</p>
              <p className="text-2xl font-bold text-green-600">
                {radiosData.filter(r => r.status === 'active').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('radios.maintenanceFaulty')}</p>
              <p className="text-2xl font-bold text-orange-600">
                {radiosData.filter(r => r.status === 'maintenance' || r.status === 'faulty').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('radios.assigned')}</p>
              <p className="text-2xl font-bold text-primary">
                {radiosData.filter(r => r.assigned_user_id).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary/20 dark:bg-primary/90 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('radios.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="all">{t('radios.allStatuses')}</option>
          {statusOptions.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Radios Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('radios.colRadio')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('radios.colLocation')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('radios.colUser')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('radios.colStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('radios.colSerialNo')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('radios.colActions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRadios.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Radio className="w-12 h-12 text-gray-300 dark:text-gray-600 dark:text-gray-400 mx-auto mb-4" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">{t('radios.noResults')}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('radios.noResultsDesc')}</p>
                  </td>
                </tr>
              ) : (
                filteredRadios.map((radio) => (
                  <tr key={radio.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Radio className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{radio.radio_name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{radio.brand} {radio.model}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 text-gray-400 mr-1 flex-shrink-0" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{radio.company?.name || '-'}</span>
                      </div>
                      <div className="flex items-center mt-1">
                        <MapPin className="w-4 h-4 text-gray-400 mr-1 flex-shrink-0" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">{radio.location?.name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {radio.assigned_user ? (
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-900 dark:text-gray-100">{radio.assigned_user.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500 dark:text-gray-400">{t('radios.notAssigned')}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        statusOptions.find(s => s.value === radio.status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}>
                        {statusOptions.find(s => s.value === radio.status)?.label || radio.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100 font-mono">{radio.serial_number}</div>
                      {radio.inventory_number && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{radio.inventory_number}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => { setSelectedRadio(radio); setShowDetailModal(true); }}
                          className="text-gray-400 hover:text-primary transition-colors"
                          title={t('radios.titleViewDetail')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(radio)}
                          className="text-gray-400 hover:text-primary transition-colors"
                          title={t('radios.titleEdit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleCopy(radio)}
                          className="text-gray-400 hover:text-green-600 transition-colors"
                          title={t('radios.titleCopy')}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => printRadioLabel(radio)}
                          className="text-gray-400 hover:text-purple-600 transition-colors"
                          title={t('radios.titlePrint')}
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        {(user?.role === 'admin' || user?.role === 'superadmin') && (
                          <button
                            onClick={() => handleDelete(radio.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title={t('radios.titleDelete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {editingRadio ? t('radios.modalEditTitle') : t('radios.modalAddTitle')}
            </h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Temel Bilgiler */}
            <div>
              <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">{t('radios.sectionBasicInfo')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelName')}</label>
                  <input
                    type="text"
                    value={radioData.radio_name}
                    onChange={(e) => setRadioData({ ...radioData, radio_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="TR-ANK-TLS-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelBrand')}</label>
                  <input
                    type="text"
                    value={radioData.brand}
                    onChange={(e) => setRadioData({ ...radioData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Motorola"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelModel')}</label>
                  <input
                    type="text"
                    value={radioData.model}
                    onChange={(e) => setRadioData({ ...radioData, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="DP4400e"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('radios.labelSerialNo')}
                    {radioData.serial_number && checkDuplicateSerialNumber(radioData.serial_number, editingRadio?.id) && (
                      <span className="ml-2 text-xs text-red-600">{t('radios.serialDuplicate')}</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={radioData.serial_number}
                    onChange={(e) => setRadioData({ ...radioData, serial_number: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                      radioData.serial_number && checkDuplicateSerialNumber(radioData.serial_number, editingRadio?.id)
                        ? 'border-red-300 bg-red-50 dark:bg-red-900/20'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="TLS2024001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelInventoryNo')}</label>
                  <input
                    type="text"
                    value={radioData.inventory_number}
                    onChange={(e) => setRadioData({ ...radioData, inventory_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="ENV-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelStatus')}</label>
                  <select
                    value={radioData.status}
                    onChange={(e) => setRadioData({ ...radioData, status: e.target.value as RadioDevice['status'] })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {statusOptions.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Teknik Özellikler */}
            <div>
              <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">{t('radios.sectionTechnical')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelFrequency')}</label>
                  <input
                    type="text"
                    value={radioData.frequency}
                    onChange={(e) => setRadioData({ ...radioData, frequency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="460 MHz"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelFrequencyRange')}</label>
                  <input
                    type="text"
                    value={radioData.frequency_range}
                    onChange={(e) => setRadioData({ ...radioData, frequency_range: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="403-527 MHz"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelPowerOutput')}</label>
                  <input
                    type="text"
                    value={radioData.power_output}
                    onChange={(e) => setRadioData({ ...radioData, power_output: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="5W"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelBatteryType')}</label>
                  <input
                    type="text"
                    value={radioData.battery_type}
                    onChange={(e) => setRadioData({ ...radioData, battery_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Li-ion 2900 mAh"
                  />
                </div>
              </div>
            </div>

            {/* Lokasyon ve Atama */}
            <div>
              <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">{t('radios.sectionLocation')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelCompany')}</label>
                  <select
                    value={radioData.company_id}
                    onChange={(e) => setRadioData({ ...radioData, company_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('radios.selectCompany')}</option>
                    {companiesData.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelLocation')}</label>
                  <select
                    value={radioData.location_id}
                    onChange={(e) => setRadioData({ ...radioData, location_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('radios.selectLocation')}</option>
                    {locationsData.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelAssignedUser')}</label>
                  <select
                    value={radioData.assigned_user_id}
                    onChange={(e) => setRadioData({ ...radioData, assigned_user_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('radios.selectUser')}</option>
                    {usersData
                      .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                      .map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Satın Alma Bilgileri */}
            <div>
              <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">{t('radios.sectionPurchase')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelPurchaseDate')}</label>
                  <input
                    type="date"
                    value={radioData.purchase_date}
                    onChange={(e) => setRadioData({ ...radioData, purchase_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelWarrantyEnd')}</label>
                  <input
                    type="date"
                    value={radioData.warranty_end_date}
                    onChange={(e) => setRadioData({ ...radioData, warranty_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelPurchasePrice')}</label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={radioData.purchase_price || ''}
                      onChange={(e) => setRadioData({ ...radioData, purchase_price: parseFloat(e.target.value) || 0 })}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="0"
                    />
                    <select
                      value={radioData.currency}
                      onChange={(e) => setRadioData({ ...radioData, currency: e.target.value as 'TRY' | 'USD' | 'EUR' })}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="TRY">₺</option>
                      <option value="USD">$</option>
                      <option value="EUR">€</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Görsel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelImage')}</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              {editingRadio?.image_path && !imageFile && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('radios.imageKeepNote')}</p>
              )}
            </div>

            {/* Notlar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('radios.labelNotes')}</label>
              <textarea
                value={radioData.notes}
                onChange={(e) => setRadioData({ ...radioData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder={t('radios.notesPlaceholder')}
              />
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
            >
              {t('radios.btnCancel')}
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              {editingRadio ? t('radios.btnUpdate') : t('radios.btnSave')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
        {selectedRadio && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('radios.detailTitle')}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedRadio.radio_name} — {selectedRadio.brand} {selectedRadio.model}
                  </p>
                </div>
                <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300">
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sol Kolon */}
                <div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">{t('radios.detailRadioInfo')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('radios.detailName')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.radio_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('radios.detailBrandModel')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.brand} {selectedRadio.model}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('radios.detailSerialNo')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.serial_number}</span>
                    </div>
                    {selectedRadio.inventory_number && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('radios.detailInventoryNo')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.inventory_number}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('radios.detailStatus')}</span>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        statusOptions.find(s => s.value === selectedRadio.status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}>
                        {statusOptions.find(s => s.value === selectedRadio.status)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('radios.detailAssignedUser')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.assigned_user?.name || t('radios.notAssigned')}</span>
                    </div>
                  </div>
                </div>

                {/* Sağ Kolon */}
                <div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">{t('radios.detailLocationTechnical')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('radios.detailCompany')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.company?.name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('radios.detailLocation')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.location?.name || '-'}</span>
                    </div>
                    {selectedRadio.frequency && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('radios.detailFrequency')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.frequency}</span>
                      </div>
                    )}
                    {selectedRadio.frequency_range && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('radios.detailFrequencyRange')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.frequency_range}</span>
                      </div>
                    )}
                    {selectedRadio.power_output && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('radios.detailPowerOutput')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.power_output}</span>
                      </div>
                    )}
                    {selectedRadio.battery_type && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('radios.detailBattery')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRadio.battery_type}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Satın Alma */}
              {(selectedRadio.purchase_date || selectedRadio.warranty_end_date || selectedRadio.purchase_price) && (
                <div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">{t('radios.detailPurchaseInfo')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {selectedRadio.purchase_date && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('radios.detailPurchaseDate')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(selectedRadio.purchase_date)}</span>
                      </div>
                    )}
                    {selectedRadio.warranty_end_date && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('radios.detailWarrantyEnd')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(selectedRadio.warranty_end_date)}</span>
                      </div>
                    )}
                    {selectedRadio.purchase_price && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('radios.detailPrice')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {selectedRadio.purchase_price.toLocaleString('tr-TR')} {selectedRadio.currency}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notlar */}
              {selectedRadio.notes && (
                <div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">{t('radios.detailNotes')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedRadio.notes}
                  </p>
                </div>
              )}

              {/* Görsel */}
              {selectedRadio.image_path && (
                <div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">{t('radios.detailImage')}</h3>
                  <div className="flex justify-center">
                    <img
                      src={`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3004'}${selectedRadio.image_path}`}
                      alt={selectedRadio.radio_name}
                      className="max-w-xs max-h-48 object-contain rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-600"
                    />
                  </div>
                </div>
              )}

              {/* Aksiyon Butonları */}
              <div className="flex justify-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => { setShowDetailModal(false); handleEdit(selectedRadio); }}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  {t('radios.btnEdit')}
                </button>
                <button
                  onClick={() => printRadioLabel(selectedRadio)}
                  className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  {t('radios.btnPrint')}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RadiosPage;
